import React, { useEffect, useMemo, useState } from "react";
import CustomChart from "../Charts";
import styles from "../../pages/Welcome/style.module.scss";

const INT_EPS = 1e-6;

function collectNumericBarValues(datasets) {
  const values = [];
  for (const ds of datasets || []) {
    for (const raw of ds.data || []) {
      const v = Number(raw);
      if (Number.isFinite(v)) values.push(v);
    }
  }
  return values;
}

function valuesLookLikeIntegerCounts(values) {
  if (!values.length) return false;
  return values.every((v) => Math.abs(v - Math.round(v)) < INT_EPS);
}

/** ~1–2–5–10 style steps so tick marks stay readable (integer only). */
function niceStepForSpan(span) {
  if (!Number.isFinite(span) || span <= 0) return 1;
  const rough = Math.max(span / 6, 1);
  const pow10 = 10 ** Math.floor(Math.log10(rough));
  const err = rough / pow10;
  let nice = 10;
  if (err <= 1) nice = 1;
  else if (err <= 2) nice = 2;
  else if (err <= 5) nice = 5;
  return nice * pow10;
}

/**
 * Linear x-axis for horizontal bars with count-like data: always integer ticks,
 * min at 0 when all values are non-negative, small headroom above max bar.
 */
function integerCountAxisFromValues(values) {
  if (!values.length) {
    return { min: 0, max: 1, stepSize: 1 };
  }
  const maxV = Math.max(...values);
  const minV = Math.min(...values);
  const hasNegative = minV < -INT_EPS;
  const minAxis = hasNegative ? Math.floor(minV + INT_EPS) : 0;
  const dataHigh = maxV;
  const span = Math.max(dataHigh - minAxis, 1);
  const step = niceStepForSpan(span);
  let axisMax = Math.ceil((dataHigh * 1.06) / step) * step;
  if (axisMax <= dataHigh) {
    axisMax = Math.ceil((dataHigh + INT_EPS) / step) * step;
  }
  if (axisMax <= minAxis) {
    axisMax = minAxis + step;
  }
  return { min: minAxis, max: axisMax, stepSize: step };
}

/**
 * KPIBarChart - Renders a bar chart for KPI data
 *
 * @param {Object} props
 * @param {Object} props.data - The chart data
 * @param {string} props.title - Optional chart title
 */
const KPIBarChart = ({ data, title, theme, isType4 = false }) => {
  const [chartData, setChartData] = useState(null);
  const [error, setError] = useState(null);

  const options = useMemo(() => {
    const tickColor = theme === "dark" ? "white" : "black";
    const gridColor =
      theme === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)";

    const datasets = chartData?.datasets;
    const numericVals = collectNumericBarValues(datasets);
    const useIntegerX =
      !isType4 &&
      numericVals.length > 0 &&
      valuesLookLikeIntegerCounts(numericVals);

    const xScale = useIntegerX
      ? integerCountAxisFromValues(numericVals)
      : null;

    return {
      responsive: true,
      indexAxis: "y",
      maintainAspectRatio: false,
      scales: {
        x: {
          grid: {
            display: false,
            drawBorder: false,
          },
          ...(isType4
            ? {
                beginAtZero: false,
                min: 1,
                max: 5,
              }
            : useIntegerX
              ? {
                  min: xScale.min,
                  max: xScale.max,
                }
              : {
                  beginAtZero: false,
                }),
          ticks: {
            padding: 20,
            color: tickColor,
            ...(isType4
              ? { stepSize: 1, precision: 0, autoSkip: false }
              : useIntegerX
                ? {
                    stepSize: xScale.stepSize,
                    precision: 0,
                    autoSkip: true,
                    maxTicksLimit: 14,
                  }
                : { autoSkip: false }),
          },
        },
        y: {
          grid: {
            display: true,
            drawBorder: false,
            color: gridColor,
          },
          ticks: {
            padding: 20,
            color: tickColor,
            autoSkip: false,
            callback(value) {
              const label = this.getLabelForValue(value);
              const maxLength = 15;
              if (label.length > maxLength) {
                return `${label.substring(0, maxLength)}..`;
              }
              return label;
            },
          },
        },
      },
      plugins: {
        legend: {
          display: true,
          position: "bottom",
          labels: {
            color: tickColor,
            usePointStyle: true,
            padding: 20,
          },
        },
        datalabels: {
          color: "white",
          font: {
            size: 11,
          },
          formatter: (value) => {
            if (value < 0.5) {
              return "";
            }
            if (useIntegerX && Math.abs(value - Math.round(value)) < INT_EPS) {
              return String(Math.round(value));
            }
            return value;
          },
          display(context) {
            return context.dataset.data[context.dataIndex] >= 0.5;
          },
          anchor: "center",
          align: "center",
          clamp: true,
        },
      },
      backgroundColor: "transparent",
      layout: {
        padding: {
          left: 10,
          right: 10,
          top: 10,
          bottom: 10,
        },
      },
    };
  }, [chartData, theme, isType4]);

  const chartDependency = useMemo(
    () => ({ chartData, options }),
    [chartData, options],
  );

  useEffect(() => {
    try {
      const processedData = data.datasets
        ? data
        : {
            labels: data.labels || [],
            datasets: [
              {
                label: data.label || "Value",
                data: data.values || [],
                backgroundColor: "rgba(54, 162, 235, 0.8)",
                borderColor: "rgba(54, 162, 235, 1)",
                borderWidth: 1,
              },
            ],
          };

      setChartData(processedData);
      setError(null);
    } catch (err) {
      console.error("Error processing chart data:", err);
      setError("Failed to process chart data");
    }
  }, [data]);

  if (error) {
    return <div className={styles.chartError}>Error: {error}</div>;
  }

  if (!chartData) {
    return <div className={styles.chartLoading}>Loading chart...</div>;
  }

  return (
    <div className={styles.chartContainer}>
      {title && <h3 className={styles.chartTitle}>{title}</h3>}
      <div style={{ height: "300px", width: "100%" }}>
        <CustomChart
          type="bar"
          data={chartData}
          options={options}
          theme={theme}
          dependency={chartDependency}
        />
      </div>
    </div>
  );
};

export default KPIBarChart;
